import React from 'react';

import { ExternalLink } from '../../../components';
import ArticleFaq from '../ArticleFaq';
import css from '../articleProse.module.css';

const IMG = '/static/images/blog';
const ARROW = '/static/images/landing/hero-cta-arrow.svg';

// Comparison scores (0-10) from the source article, rendered as CSS bars so the
// values stay readable in the DOM for search and AI answer engines.
const COMPARISON_COLUMNS = ['Selection Size', 'Safety', 'Search', 'Guidance', 'Cost Efficiency'];
const COMPARISON_ROWS = [
  { name: 'Avento', scores: [7, 10, 10, 3, 9] },
  { name: 'Facebook', scores: [9, 1, 1, 1, 10] },
  { name: 'eBay', scores: [4, 5, 4, 1, 3] },
  { name: 'Locally', scores: [2, 10, 1, 7, 10] },
  { name: 'Rigger / Gearshop', scores: [3, 10, 5, 10, 6] },
];

// Q&A content; BlogArticlePage builds the FAQPage JSON-LD from this same array
// so the schema text always matches the visible answers.
export const faq = [
  {
    question: 'Is it safe to buy used skydiving gear?',
    answer:
      'Yes, if you control two things: who you are paying and who inspects the gear. Most gear ' +
      'in the sport changes hands second hand and there is nothing wrong with that. A container ' +
      'or canopy with a documented history and a clean rigger inspection is not worse than new. ' +
      'What makes it unsafe is paying a stranger with no protection and jumping the gear without ' +
      'having a rigger look at it first.',
  },
  {
    question: 'How do I avoid getting scammed buying gear on Facebook?',
    answer:
      'Verify every hard fact in writing before you pay: jumps on the canopy, jumps on the ' +
      'lineset, repairs and patches. Do a live video call with the seller holding the gear, not ' +
      'a photo with a handwritten note. Find someone in the community who can vouch for them, ' +
      'and never let anyone rush you. None of this makes you safe, it only lowers the risk, ' +
      'which is why payment protection matters on top of it.',
  },
  {
    question: 'Should I buy used gear from an Instagram page or a used gear website?',
    answer:
      'Be very careful. Most general "used skydiving gear" Instagram pages and websites are ' +
      'scams. Unless it is an established shop where you know exactly who is behind it, do not ' +
      'buy there.',
  },
  {
    question: 'How much does used skydiving gear cost?',
    answer: (
      <>
        For a complete system, expect to pay anywhere between roughly $3,000 and $12,000, depending
        on whether you buy used or new and what exactly you are looking at. Prices vary a lot with
        age, jump numbers, size and demand for the specific model, so our{' '}
        <a href="/gear-value-calculator" className={css.link}>
          Gear Value Calculator
        </a>{' '}
        gives you a range for a specific item.
      </>
    ),
  },
  {
    question: 'Is dropzone.com still a place to buy skydiving gear?',
    answer:
      'No. The dropzone.com classifieds were the main channel for used gear for years, though ' +
      'it was never much more than a basic forum. The site is gone now and the domain redirects ' +
      'somewhere unrelated, but many older guides still recommend it.',
  },
];

// Body for /blog/where-to-buy-used-skydiving-gear.
// Copy is taken verbatim from the source article.
const WhereToBuyUsedSkydivingGear = () => (
  <div className={css.prose}>
    <p className={css.lead}>
      For newly licensed jumpers looking for their first rig, but also for experienced skydivers
      looking to buy a new specific parachute, the question of where exactly to buy used skydiving
      gear is often confusing. What makes finding the right equipment so complicated is that our
      gear is extremely specific which makes it rather unlikely that your buddy from the dropzone is
      selling exactly what you need.
    </p>

    <h2>Key Takeaways</h2>
    <p>
      Used skydiving gear can be bought through online marketplaces, Facebook groups, local
      communities, and riggers or gear shops trading pre-owned equipment, each with its own
      trade-offs regarding price, selection, safety, and service. Specific online marketplaces like
      Avento offer a large selection, competitive prices, secure payments with buyer protection, and
      the ability to search for exact items, but buyers don't get personal guidance in selecting
      gear. Local purchases from a rigger or friend offer the possibility to physically inspect the
      gear and expert advice and test jumps, but the selection is smaller and finding the perfect
      item depends on luck. Facebook groups are free and the most commonly used option, but
      searching for the right gear is difficult and fraud is a major problem because of missing
      payment protection.
    </p>
    <ul>
      <li>
        Avento is highlighted as a top option for pre-owned skydiving gear as it is the only
        marketplace specifically built for used skydiving gear. Secure escrow payments, specific
        search functions, and the option to let your rigger inspect the gear.
      </li>
      <li>
        Direct purchases from friends and local skydivers are a great option if you are lucky enough
        to find the right item there. Possibility to try the gear on in person, personal pickup and
        no fees.
      </li>
      <li>
        Purchasing directly from a rigger or dealer trading used gear offers personalised advice and
        inspection, but cost tends to be higher and the selection limited.
      </li>
      <li>
        Facebook groups are the most common option with the largest selection but requires patience,
        searching for the needle in a haystack, and the risk of scams is high.
      </li>
      <li>
        General Online Marketplaces like eBay or Craigslist are best avoided. The filtering- and
        search-functions are not specific enough to find the right gear, and eBay's high fees make
        gear very expensive which is why they are generally unused by experienced skydivers and the
        remaining listings often low-quality.
      </li>
      <li>
        In any case, ask your rigger for advice and guidance, especially as a beginner. They will
        help you with tips, know your level, and can prevent you from making bad decisions.
      </li>
    </ul>

    <h2>What's Ahead</h2>
    <ul className={css.tocPlain}>
      <li>
        <a href="#local-purchases" className={css.tocLink}>
          1. Local purchases: Personal but limited
        </a>
      </li>
      <li>
        <a href="#rigger-gearshops" className={css.tocLink}>
          2. Rigger &amp; Gearshops: Individual guidance
        </a>
      </li>
      <li>
        <a href="#facebook-groups" className={css.tocLink}>
          3. Facebook buy/sell groups: Largest selection but high-risk
        </a>
      </li>
      <li>
        <a href="#ebay" className={css.tocLink}>
          4. eBay: Almost never used
        </a>
      </li>
      <li>
        <a href="#avento" className={css.tocLink}>
          5. Avento: Safe and specialised for used skydiving gear
        </a>
      </li>
      <li>
        <a href="#comparison" className={css.tocLink}>
          Comparison: Best place to buy your skydiving gear
        </a>
      </li>
      <li>
        <a href="#qa" className={css.tocLink}>
          Q&amp;A
        </a>
      </li>
    </ul>

    <h2 id="local-purchases">1. Local purchases: Personal but limited</h2>
    <p>
      If you are looking for common used gear such as a main parachute or a medium sized container
      with average harness measurements, looking locally is the best place to start. Ask around at
      the DZ, many dropzones have local WhatsApp or Facebook groups where you could ask. Also
      approach the local rigger, they often know who is selling what and often sell gear on behalf
      of their customers as well.
    </p>
    <p>
      If you are lucky enough to find the perfect gear for you locally, this is the best option. You
      can get a rigger or instructor to inspect and try on the gear in person before buying, don't
      have to worry about shipping, and there is no middleman taking any fees.
    </p>
    <p>
      Just make sure to also have a look at what is available online. Unfortunately sometimes
      jumpers, or even instructors try to sell their old gear to an unknowing young jumper for an
      unrealistically high price.
    </p>
    <ul>
      <li>
        <strong>Pros:</strong> Easy, cheap, in-person inspection, test-jumping
      </li>
      <li>
        <strong>Cons:</strong> Limited selection, especially if you are looking for something very
        specific like a rig
      </li>
    </ul>

    <h2 id="rigger-gearshops">2. Rigger &amp; Gearshops: Individual Guidance</h2>
    <p>
      Some professional rigging- and gear-shops also trade used gear on behalf of their customers.
      Our partners{' '}
      <ExternalLink className={css.link} href="https://skyvoss.no">
        Skyvoss AS
      </ExternalLink>
      , and{' '}
      <ExternalLink className={css.link} href="https://mind-fly.de">
        Mindfly
      </ExternalLink>{' '}
      are professional rigging and gearshops that are also actively trading used gear.
    </p>
    <p>
      Although the selection is still rather limited, these are great options as the gear was
      inspected by an experienced rigger and you get personal expert-advice on what gear is right
      for you. Naturally, the prices are possibly a bit higher because of the service they provide
      but the safety, consultation and peace of mind is worth it.
    </p>
    <ul>
      <li>
        <strong>Pros:</strong> Expert-advice, peace of mind, rigger inspected gear
      </li>
      <li>
        <strong>Cons:</strong> Limited selection
      </li>
    </ul>

    <h2 id="facebook-groups">3. Facebook buy/sell groups: Largest selection but high-risk</h2>
    <p>
      The currently most-used platform to buy used gear online are Facebook buy/sell groups. Most
      jumpers don't like the hassle but they undeniably have the largest selection of used-gear if
      you are patient enough for the right gear to come up.
    </p>
    <p>Some specific groups that are commonly recommended are:</p>
    <ul>
      <li>Skydiving Gear for Sale</li>
      <li>Skydiving Gear For Sale and Wanted</li>
      <li>Buy/Sell Skydiving Gear</li>
      <li>USA Skydiving Gear Sales</li>
    </ul>
    <p>
      Keep in mind two important things when buying used gear on Facebook: Firstly, fraud is very
      common and has become an increasingly relevant issue in recent years. Scammers caught on to
      the fact that large payments are made without any actual safety, and started to become quite
      good at faking listings and impersonating skydivers. According to a survey we ran, around 1 in
      3 skydivers who traded gear online have experienced fraud attempts before.
    </p>
    <p>
      Secondly, you can't search or filter, so stay patient and consistently check what has been
      uploaded. You can make a "searching for &hellip;" post, with as much detail of what you are
      looking for as possible, but be cautious as these tend to especially attract scammers.
    </p>
    <p>
      We have held a presentation on how to spot and avoid scams when buying gear on Facebook at the
      European Skydiving Symposium 2026 in Sevilla. You can check out the{' '}
      <ExternalLink className={css.link} href="https://youtu.be/lVgAAGydlKU">
        recording of the speech
      </ExternalLink>{' '}
      if interested.
    </p>
    <figure className={css.figure}>
      <img
        src={`${IMG}/ess-2026-skydiving-gear-scams-talk.webp`}
        alt="Lean Stein presenting on skydiving gear scams at the European Skydiving Symposium 2026"
        width={1600}
        height={700}
        loading="lazy"
      />
    </figure>
    <ul>
      <li>
        <strong>Pros:</strong> Largest selection, no fees
      </li>
      <li>
        <strong>Cons:</strong> Searching for the needle in a haystack, scams are a major problem
      </li>
    </ul>

    <h2 id="ebay">4. eBay: Almost never used</h2>
    <p>
      Although eBay, and other general online marketplaces like Craigslist, are very well-known for
      trading used goods of all sorts, they are very unsuitable for the sale of used skydiving gear
      and almost never used.
    </p>
    <p>
      Because of their generalist nature, they cannot even reliably tell skydiving gear apart from
      paragliding gear, let alone let you search for the specific gear you are looking for.
      Furthermore the{' '}
      <ExternalLink
        className={css.link}
        href="https://www.ebay.com/help/selling/fees-credits-invoices/selling-fees?id=4364"
      >
        fee structure is very high
      </ExternalLink>{' '}
      (13.6% on total amount of the sale up to $7,500) which deters good sellers from selling over
      eBay. As a result, much of the skydiving gear available on eBay is low quality or in bad
      condition. It even became a bit of a running joke in the skydiving community that eBay
      listings are "30 years old but still crispy".
    </p>
    <p>
      eBay does provide some payment protection, but it is not built for a transaction like this. As
      the customer support staff are not skydivers, their knowledge about the items is largely
      non-existent. Furthermore the inspection windows and transaction process is not designed for
      skydiving gear.
    </p>
    <ul>
      <li>
        <strong>Pros:</strong> Well-known, many people already have an account
      </li>
      <li>
        <strong>Cons:</strong> Unsuitable for skydiving equipment, very high fees, almost never used
        by good sellers
      </li>
    </ul>

    <h2 id="avento">5. Avento: Safe and specialised for used skydiving gear</h2>
    <p>
      <a href="/" className={css.link}>
        Avento
      </a>{' '}
      is built specifically for skydiving gear. Through safe escrow payments, the payment is
      securely held until you received the gear and had time to bring it to your rigger for
      inspection. If anything is not as described, you can dispute the transaction and receive a
      full refund. With the filtering and search algorithm you can find exactly what you are
      looking for, and set alerts for specific searches to be notified when something suitable is
      uploaded.
    </p>
    <div className={css.mediaRow}>
      <div className={css.mediaRowText}>
        <p>
          There are hundreds of listings available in various categories such as{' '}
          <a href="/s/complete-systems" className={css.link}>
            complete rigs
          </a>
          ,{' '}
          <a href="/s/rigs" className={css.link}>
            containers
          </a>
          ,{' '}
          <a href="/s/main-canopies" className={css.link}>
            main canopies
          </a>
          , and{' '}
          <a href="/s/helmets-and-equipment" className={css.link}>
            helmets
          </a>
          , and a very active user-base of skydivers and riggers.
        </p>
        <p>
          To help you with buying the right used gear, we have gone to great lengths to build
          resources such as the{' '}
          <a href="/harness-sizing" className={css.link}>
            Harness Sizing Guide
          </a>
          ,{' '}
          <a href="/gear-value-calculator" className={css.link}>
            Gear Value Calculator
          </a>{' '}
          or{' '}
          <a href="/container-sizing" className={css.link}>
            Container Sizing Guide
          </a>
          . Nevertheless, we cannot provide you personal 1:1 guidance as we do not know you or your
          level. This is why you should still consult your rigger and show him a listing before
          purchasing. Avento is not a replacement for personal guidance from your rigger or
          instructor but makes it easier for you and your rigger to find the right gear for you.
        </p>
      </div>
      <div className={css.mediaRowImage}>
        <img
          src={`${IMG}/avento-used-skydiving-gear-marketplace-mobile.webp`}
          alt="Avento used skydiving gear marketplace on mobile"
          width={483}
          height={936}
          loading="lazy"
        />
      </div>
    </div>
    <ul>
      <li>
        <strong>Pros:</strong> Built specifically for skydiving, secure transactions with buyer
        protection, easy search, low{' '}
        <a href="/fees" className={css.link}>
          fees
        </a>
        , large inventory
      </li>
      <li>
        <strong>Cons:</strong> No personal guidance when buying gear, fewer listings than Facebook
        (for now..)
      </li>
    </ul>
    <div className={css.ctaRow}>
      <a href="/s" className={css.ctaButton}>
        Browse Avento
        <img src={ARROW} alt="" className={css.ctaArrow} width={20} height={20} />
      </a>
    </div>

    <aside className={css.aside}>
      <p>
        <strong>Transparency Note:</strong> We are ourselves the founders of Avento which could of
        course mean that we are a bit biased. Nevertheless, we've made an effort to write this
        article from a neutral perspective. After all, the issues with the current options like
        Facebook groups have been the exact reason that we built Avento.
      </p>
    </aside>

    <h2 id="comparison">Comparison: Best place to buy your skydiving gear</h2>
    <div className={css.tableScroll}>
      <table className={css.barTable} aria-describedby="comparison-scale">
        <thead>
          <tr>
            <th scope="col" aria-label="Buying option" />
            {COMPARISON_COLUMNS.map(column => (
              <th scope="col" key={column}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map(row => (
            <tr key={row.name}>
              <th scope="row">{row.name}</th>
              {row.scores.map((score, i) => (
                <td key={COMPARISON_COLUMNS[i]}>
                  <div className={css.barCell}>
                    <div className={css.barTrack}>
                      <div className={css.barFill} style={{ width: `${score * 10}%` }} />
                    </div>
                    <span className={css.srOnly}>{score} out of 10</span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p id="comparison-scale" className={css.srOnly}>
      Scores on a 0 to 10 scale: 0 is the worst, 10 the best possible rating.
    </p>
    <p>
      Each option has advantages and disadvantages. In the end it comes down to you and what you
      need.
    </p>

    <h2 id="qa">Q&amp;A</h2>
    <ArticleFaq faq={faq} />

    <div className={css.ctaRow}>
      <a href="/blog" className={css.ctaButton}>
        All Articles
        <img src={ARROW} alt="" className={css.ctaArrow} width={20} height={20} />
      </a>
    </div>
  </div>
);

export default WhereToBuyUsedSkydivingGear;
